import React, { useContext, useEffect } from "react";
import { useTutorial, TutorialStep } from "./TutorialContext";
import TutorialTooltip from "./TutorialTooltip";
import styles from "./TutorialOverlay.module.css";
import { InterviewContext } from "../../../contexts/InterviewContext";

const TutorialOverlay: React.FC = () => {
  const {
    isActive,
    currentStep,
    nextStep,
    prevStep,
    endTutorial,
    isAudioPlaying,
    skipAudio,
    isAwaitingAction,
    completeAction,
    canProceed,
  } = useTutorial();

  // Get microphone button click handler from InterviewContext
  const interviewContext = useContext(InterviewContext);

  // Monitor for microphone button clicks during interactive steps
  useEffect(() => {
    if (!isActive || !currentStep) return;

    // For the microphone interactive step, we need to hook into the finishAnsweringHandler
    if (
      currentStep === TutorialStep.MICROPHONE_INTERACTIVE &&
      isAwaitingAction
    ) {
      // Add event listener to microphone button
      const microphoneButton = document.querySelector(
        'button[aria-label*="microphone" i], button[title*="microphone" i], button[class*="mic" i]'
      ) as HTMLElement;

      if (microphoneButton) {
        const handleMicrophoneClick = () => {
          completeAction("microphone");
        };

        microphoneButton.addEventListener("click", handleMicrophoneClick);

        return () => {
          microphoneButton.removeEventListener("click", handleMicrophoneClick);
        };
      }
    }
  }, [currentStep, isActive, isAwaitingAction, completeAction]);

  if (!isActive || !currentStep) return null;

  // Define the type for tutorial content items
  type TutorialContentItem = {
    title: string;
    content: React.ReactNode;
    targetSelector: string;
    position: "bottom" | "top" | "left" | "right";
    requiredAction?: string;
  };

  // Tutorial content for each step with updated selectors
  const tutorialContent: Record<TutorialStep, TutorialContentItem> = {
    [TutorialStep.WELCOME]: {
      title: "Welcome to Mock Interview",
      content: (
        <p>
          Welcome to your mock case interview! This tutorial will guide you
          through all the elements of the interface to help you get comfortable.
          Let's begin!
        </p>
      ),
      targetSelector: "body", // Target the body instead of appContainer
      position: "top" as const,
    },
    [TutorialStep.TIMELINE]: {
      title: "Interview Timeline",
      content: (
        <p>
          This timeline shows your progress through the case interview. Each
          node represents a different question or section. The highlighted node
          is your current position in the interview.
        </p>
      ),
      targetSelector: '[class*="timeline" i]', // More general selector
      position: "bottom" as const,
    },
    [TutorialStep.INTERVIEWER]: {
      title: "AI Interviewer",
      content: (
        <p>
          This is your AI interviewer. The avatar will pulse blue when the
          interviewer is speaking to you. When it's your turn to speak, the
          pulsing will stop, indicating that you should provide your answer.
        </p>
      ),
      targetSelector: '[class*="interviewer" i]', // More general selector
      position: "top" as const,
    },
    [TutorialStep.CAMERA]: {
      title: "Camera Toggle",
      content: (
        <p>
          This button allows you to turn your camera on or off. Having your
          camera on provides a more realistic interview experience, but you can
          disable it if needed.
        </p>
      ),
      targetSelector:
        'button[aria-label*="camera" i], button[title*="camera" i], button[class*="camera" i]',
      position: "top" as const,
    },
    [TutorialStep.GRAPHS]: {
      title: "Graph Display",
      content: (
        <p>
          During the interview, I may display graphs, charts, or other visual
          information relevant to the case. You can navigate between multiple
          graphs using the arrow buttons if they appear.
        </p>
      ),
      targetSelector: '[class*="interviewer" i]',
      position: "left" as const,
    },
    [TutorialStep.SKIP_QUESTION]: {
      title: "Skip Question Button",
      content: (
        <p>
          If you want to skip the current question, you can use this button.
          This will move you to the next question in the interview, but remember
          that skipping questions may affect your overall interview performance.
        </p>
      ),
      targetSelector:
        'button[aria-label*="skip" i], button[title*="skip" i], button[class*="skip" i]',
      position: "top" as const,
    },
    [TutorialStep.MICROPHONE]: {
      title: "Microphone Button",
      content: (
        <p>
          This button is disabled when I'm speaking. When it's your turn, it
          will start recording automatically. Feel free to take the time to
          structure your thought. Click finished talking when you are done!
        </p>
      ),
      targetSelector:
        'button[aria-label*="microphone" i], button[title*="microphone" i], button[class*="mic" i]',
      position: "top" as const,
    },
    [TutorialStep.MICROPHONE_INTERACTIVE]: {
      title: "Try the Microphone",
      content: (
        <p>
          Let's practice using the microphone button. When this step is
          complete, click the microphone button as if you were going to answer a
          question. This simulates what you'll do when the interviewer asks you
          a question.
        </p>
      ),
      targetSelector:
        'button[aria-label*="microphone" i], button[title*="microphone" i], button[class*="mic" i]',
      position: "top" as const,
      requiredAction: "microphone",
    },
    [TutorialStep.COMPLETE]: {
      title: `You're Ready!`,
      content: (
        <p>
          Great! You now understand all the key elements of the mock interview
          interface. The interview will begin with an introduction from your AI
          interviewer. Good luck with your case interview!
        </p>
      ),
      targetSelector:
        'button[aria-label*="microphone" i], button[title*="microphone" i], button[class*="mic" i]',
      position: "left" as const,
    },
  };

  // Get current step content
  const currentContent = tutorialContent[currentStep];
  const isLastStep = currentStep === TutorialStep.COMPLETE;

  // Simulate a mock interview response for the interactive microphone step
  const simulateInterviewerResponse = () => {
    if (
      currentStep === TutorialStep.MICROPHONE_INTERACTIVE &&
      interviewContext
    ) {
      const { playAudioByContent } = interviewContext;
      if (playAudioByContent) {
        setTimeout(() => {
          playAudioByContent(
            "Perfect! That's how you'll interact with me during the interview. Let's continue with the tutorial."
          );
        }, 1000);
      }
    }
  };

  // Handle microphone action completion
  const handleActionComplete = (action: string) => {
    completeAction(action);

    if (action === "microphone") {
      simulateInterviewerResponse();
    }
  };

  return (
    <div className={styles.tutorialOverlay}>
      <div className={styles.tutorialBackground}></div>

      <TutorialTooltip
        targetSelector={currentContent.targetSelector}
        title={currentContent.title}
        content={currentContent.content}
        position={currentContent.position}
        onNext={nextStep}
        onPrev={prevStep}
        onClose={endTutorial}
        onSkipAudio={skipAudio}
        showPrev={currentStep !== TutorialStep.WELCOME}
        showNext={true}
        showClose={true}
        isLastStep={isLastStep}
        isAudioPlaying={isAudioPlaying}
        isAwaitingAction={isAwaitingAction}
        requiredAction={currentContent.requiredAction}
        canProceed={canProceed}
      />
    </div>
  );
};

export default TutorialOverlay;
